import api from './api.service';

export const employeeKeys = {
  all: ['hr-employees'] as const,
  list: (filters?: object) => [...employeeKeys.all, 'list', filters] as const,
  detail: (id: string) => [...employeeKeys.all, 'detail', id] as const,
  emergency: (employeeId: string) => [...employeeKeys.all, 'emergency', employeeId] as const,
  profileRequests: (employeeId?: string) =>
    [...employeeKeys.all, 'profile-requests', employeeId] as const,
  leaveBalances: (employeeId: string) => [...employeeKeys.all, 'leave-balances', employeeId] as const,
  salaryComponents: (employeeId: string) => [...employeeKeys.all, 'salary-components', employeeId] as const,
  qualifications: (employeeId: string) => [...employeeKeys.all, 'qualifications', employeeId] as const,
  performance: (employeeId: string) => [...employeeKeys.all, 'performance', employeeId] as const,
  lifecycle: (employeeId: string) => [...employeeKeys.all, 'lifecycle', employeeId] as const,
  shiftAssignments: (employeeId: string) => [...employeeKeys.all, 'shift-assignments', employeeId] as const,
  shiftOverview: () => [...employeeKeys.all, 'shift-overview'] as const,
  workWeekOverview: () => [...employeeKeys.all, 'work-week-overview'] as const,
};

function unwrap<T>(response: { data?: any }): T {
  const d = response?.data?.data ?? response?.data;
  return d as T;
}

export interface EmployeeRecord {
  id: string;
  company_id: string;
  /** Linked login user, when the employee has system access. */
  user_id?: string | null;
  employee_code: string;
  first_name: string;
  last_name: string;
  middle_name?: string;
  email: string;
  personal_email?: string;
  mobile?: string;
  date_of_birth: string;
  gender: string;
  nationality?: string;
  passport_number?: string;
  emirates_id?: string;
  /** India PAN (TDS / Form 16). */
  pan_number?: string | null;
  /** India TDS regime NEW | OLD. */
  tax_regime?: string | null;
  /** EPFO UAN. */
  uan_number?: string | null;
  /** ESIC number. */
  esi_number?: string | null;
  visa_expiry_date?: string;
  department_id?: string;
  designation_id?: string;
  reporting_to_id?: string;
  date_of_joining: string;
  date_of_leaving?: string;
  employment_status: string;
  profile_version?: number;
  employment_type?: string;
  basic_salary: number;
  currency: string;
  bank_name?: string;
  bank_account_number?: string;
  iban?: string;
  address?: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  name_ar?: string;
  marital_status?: string;
  religion?: string;
  blood_group?: string;
  country_of_birth?: string;
  place_of_birth?: string;
  home_phone?: string;
  alternate_phone?: string;
  photo_url?: string;
  current_address_json?: string;
  permanent_address_json?: string;
  emirates_id_expiry?: string;
  passport_issue_date?: string;
  passport_expiry_date?: string;
  passport_place_of_issue?: string;
  visa_number?: string;
  visa_type?: string;
  visa_issue_date?: string;
  visa_sponsor?: string;
  visa_status?: string;
  labour_card_number?: string;
  labour_card_expiry?: string;
  labour_card_job_title?: string;
  insurance_provider?: string;
  insurance_policy_number?: string;
  insurance_expiry?: string;
  uid_number?: string;
  work_permit_number?: string;
  work_permit_issue_date?: string;
  work_permit_expiry_date?: string;
  branch_id?: string;
  location_id?: string | null;
  cost_center_code?: string;
  grade?: string;
  position_id?: string | null;
  job_title?: string;
  confirmation_date?: string;
  confirmation_status?: string;
  last_duty_resume_date?: string;
  probation_months?: number;
  contract_type?: string;
  contract_start_date?: string;
  contract_end_date?: string;
  notice_period_days?: number;
  work_schedule?: string;
  shift_type?: string;
  exclude_from_attendance_rules?: boolean;
  termination_date?: string;
  termination_reason?: string;
  rehire_eligible?: boolean;
  payment_method?: string;
  salary_effective_date?: string;
  swift_code?: string;
  bank_branch_name?: string;
  bank_branch_code?: string;
  wps_person_code?: string;
  holiday_calendar_id?: string;
  default_shift_id?: string;
}

export interface EmployeeQualificationRecord {
  id: string;
  employee_id: string;
  qual_type: string;
  title: string;
  institution?: string | null;
  credential_number?: string | null;
  issued_on?: string | null;
  expiry_date?: string | null;
  level_or_grade?: string | null;
  country?: string | null;
  notes?: string | null;
  is_verified?: boolean;
}

export interface EmployeePerformanceRecord {
  id: string;
  employee_id: string;
  record_type: string;
  title: string;
  period_year?: number | null;
  period_label?: string | null;
  status: string;
  rating?: string | null;
  manager_id?: string | null;
  due_date?: string | null;
  completed_date?: string | null;
  summary?: string | null;
  goals_json?: string | null;
}

export interface EmployeeLifecycleEventRecord {
  id: string;
  employee_id: string;
  event_type: string;
  event_date: string;
  effective_from?: string | null;
  effective_to?: string | null;
  from_department_id?: string | null;
  to_department_id?: string | null;
  from_location_id?: string | null;
  to_location_id?: string | null;
  from_job_title?: string | null;
  to_job_title?: string | null;
  from_grade?: string | null;
  to_grade?: string | null;
  reason?: string | null;
  outcome?: string | null;
  status: string;
  document_ref?: string | null;
}

export interface EmployeeShiftAssignmentRecord {
  id: string;
  employee_id: string;
  shift_id: string;
  effective_from: string;
  effective_to?: string | null;
}

export type EmployeePayload = Partial<
  Omit<EmployeeRecord, 'id' | 'company_id' | 'created_at' | 'updated_at' | 'basic_salary'>
> & {
  employee_code?: string;
  first_name: string;
  last_name: string;
  email: string;
  date_of_birth: string;
  gender: string;
  date_of_joining: string;
  employment_status: string;
  /** Owned by salary revisions — omit on create/update from employee form */
  basic_salary?: number;
};

export interface EmergencyContactRecord {
  id: string;
  employee_id: string;
  contact_name: string;
  relationship: string;
  phone_primary: string;
  phone_secondary?: string;
  email?: string;
  address?: string;
  is_primary: boolean;
}

export interface ProfileChangeRequestRecord {
  id: string;
  employee_id: string;
  field_key: string;
  old_value_json?: string;
  new_value_json?: string;
  status: string;
  review_comment?: string;
  created_at?: string;
}

export interface LeaveBalanceRecord {
  id: string;
  employee_id: string;
  leave_year: number;
  leave_type: string;
  opening_balance: number;
  accrued?: number;
  consumed: number;
  carried_forward: number;
  pending?: number;
  encashed?: number;
  available_days?: number;
  balance_days?: number;
  entitled_days?: number;
  taken_days?: number;
}

export interface SalaryComponentRecord {
  id: string;
  employee_id: string;
  component_code: string;
  component_name: string;
  amount: number;
  effective_from: string;
  effective_to?: string;
}

export type EmployeeListParams = {
  department_id?: string;
  employment_status?: string;
  employment_type?: string;
  /** A location id, or `none` for employees without a work location. */
  location_id?: string;
  search?: string;
};

export type LocationAssignmentResult = {
  changed: number;
  unchanged: number;
  skipped: { employee_code: string; reason: string }[];
  pending_profile_requests: string[];
};

export type ShiftAssignmentResult = {
  changed: number;
  unchanged: number;
  defaults_updated: number;
  skipped: { employee_code: string; reason: string }[];
  /** Generated Draft / Rejected timesheet months inside the changed period. */
  regenerate_months: { period: string; employees: number }[];
};

export type EmployeeShiftOverview = {
  employee_id: string;
  default_shift_id: string | null;
  current_shift_id: string | null;
  current_source: 'assignment' | 'default' | null;
  current_until: string | null;
  next_shift_id: string | null;
  next_from: string | null;
};

export type WorkWeekAssignmentResult = {
  changed: number;
  unchanged: number;
  skipped: { employee_code: string; reason: string }[];
  /** Generated Draft / Rejected timesheet months inside the changed period. */
  regenerate_months: { period: string; employees: number }[];
};

export type EmployeeWorkWeekOverview = {
  employee_id: string;
  default_work_week: string;
  default_source: 'location' | 'company';
  current_work_week: string;
  current_source: 'assignment' | 'location' | 'company';
  current_until: string | null;
  next_work_week: string | null;
  next_from: string | null;
};

class EmployeeService {
  async list(params?: EmployeeListParams): Promise<EmployeeRecord[]> {
    const response = await api.get('/hr/employees', { params });
    const raw = unwrap<EmployeeRecord[]>(response);
    return Array.isArray(raw) ? raw : [];
  }

  async getById(id: string): Promise<EmployeeRecord> {
    const response = await api.get(`/hr/employees/${id}`);
    return unwrap<EmployeeRecord>(response);
  }

  async create(body: EmployeePayload): Promise<EmployeeRecord> {
    const response = await api.post('/hr/employees', body);
    return unwrap<EmployeeRecord>(response);
  }

  async update(
    id: string,
    body: Partial<EmployeePayload>
  ): Promise<
    | EmployeeRecord
    | {
        mode: 'pending_approval';
        employee: EmployeeRecord;
        change_request: ProfileChangeRequestRecord;
        message?: string;
      }
  > {
    const response = await api.put(`/hr/employees/${id}`, body);
    const raw = response?.data ?? response;
    const payload = raw?.data ?? raw;
    if (payload?.mode === 'pending_approval') {
      return {
        mode: 'pending_approval',
        employee: payload.employee,
        change_request: payload.change_request,
        message: raw?.message,
      };
    }
    return unwrap<EmployeeRecord>(response);
  }

  async remove(id: string): Promise<void> {
    await api.delete(`/hr/employees/${id}`);
  }

  async uploadPhoto(id: string, file: File): Promise<{ photo_url: string }> {
    const formData = new FormData();
    formData.append('photo', file);
    const response = await api.post(`/hr/employees/${id}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap<{ photo_url: string }>(response);
  }

  async assignLocation(body: {
    employee_ids: string[];
    location_id: string | null;
    effective_date?: string;
    reason?: string;
  }): Promise<LocationAssignmentResult> {
    const response = await api.post('/hr/employees/location-assignment', body);
    return unwrap<LocationAssignmentResult>(response);
  }

  async downloadLocationAssignmentTemplateBlob(): Promise<Blob> {
    const response = await api.getClient().get('/hr/employees/location-assignment/template', {
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  async exportLocationAssignmentsBlob(params?: EmployeeListParams): Promise<Blob> {
    const response = await api.getClient().get('/hr/employees/location-assignment/export', {
      params,
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  async importLocationAssignments(file: File): Promise<LocationAssignmentResult> {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post('/hr/employees/location-assignment/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap<LocationAssignmentResult>(response);
  }

  async shiftOverview(): Promise<EmployeeShiftOverview[]> {
    const response = await api.get('/hr/employees/shift-assignment/overview');
    return unwrap<EmployeeShiftOverview[]>(response) || [];
  }

  async assignShift(body: {
    employee_ids: string[];
    shift_id: string | null;
    effective_from: string;
    effective_to?: string | null;
    set_default?: boolean;
  }): Promise<ShiftAssignmentResult> {
    const response = await api.post('/hr/employees/shift-assignment', body);
    return unwrap<ShiftAssignmentResult>(response);
  }

  async downloadShiftAssignmentTemplateBlob(): Promise<Blob> {
    const response = await api.getClient().get('/hr/employees/shift-assignment/template', {
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  async exportShiftAssignmentsBlob(params?: EmployeeListParams): Promise<Blob> {
    const response = await api.getClient().get('/hr/employees/shift-assignment/export', {
      params,
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  async importShiftAssignments(file: File): Promise<ShiftAssignmentResult> {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post('/hr/employees/shift-assignment/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap<ShiftAssignmentResult>(response);
  }

  async workWeekOverview(): Promise<EmployeeWorkWeekOverview[]> {
    const response = await api.get('/hr/employees/work-week-assignment/overview');
    return unwrap<EmployeeWorkWeekOverview[]>(response) || [];
  }

  async assignWorkWeek(body: {
    employee_ids: string[];
    work_week: string | null;
    effective_from: string;
    effective_to?: string | null;
  }): Promise<WorkWeekAssignmentResult> {
    const response = await api.post('/hr/employees/work-week-assignment', body);
    return unwrap<WorkWeekAssignmentResult>(response);
  }

  async downloadWorkWeekAssignmentTemplateBlob(): Promise<Blob> {
    const response = await api.getClient().get('/hr/employees/work-week-assignment/template', {
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  async exportWorkWeekAssignmentsBlob(params?: EmployeeListParams): Promise<Blob> {
    const response = await api.getClient().get('/hr/employees/work-week-assignment/export', {
      params,
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  async importWorkWeekAssignments(file: File): Promise<WorkWeekAssignmentResult> {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post('/hr/employees/work-week-assignment/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap<WorkWeekAssignmentResult>(response);
  }

  async exportCsvBlob(params?: EmployeeListParams): Promise<Blob> {
    const client = api.getClient();
    const response = await client.get('/hr/employees/export', {
      params,
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  async downloadImportTemplateAsFile(): Promise<void> {
    const client = api.getClient();
    const response = await client.get('/hr/employees/import-template.csv', {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'text/csv; charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employees-import-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  async importFromCsv(
    file: File
  ): Promise<{ created: number; updated: number; errors: string[]; skipped: number }> {
    const formData = new FormData();
    formData.append('file', file);
    // Do not set Content-Type manually — browser/axios must add the multipart boundary.
    try {
      const response = await api.post('/hr/employees/import', formData, {
        timeout: 300_000,
      });
      const body = response as {
        success?: boolean;
        message?: string;
        data?: { created?: number; updated?: number; errors?: string[]; skipped?: number };
      };
      const importData = body?.data ?? (body as any);
      const result = {
        created: Number(importData?.created) || 0,
        updated: Number(importData?.updated) || 0,
        skipped: Number(importData?.skipped) || 0,
        errors: Array.isArray(importData?.errors) ? (importData.errors as string[]) : [],
      };
      if (body?.success === false && result.created + result.updated === 0) {
        // Still return structured result so the page can open the errors modal.
        if (result.errors.length === 0 && body.message) {
          result.errors = [body.message];
        }
      }
      return result;
    } catch (err: unknown) {
      const ax = err as {
        response?: {
          data?: {
            message?: string;
            error?: string;
            data?: { created?: number; updated?: number; errors?: string[]; skipped?: number };
          };
        };
        message?: string;
      };
      const body = ax.response?.data;
      const importData = body?.data;
      if (importData && Array.isArray(importData.errors)) {
        return {
          created: Number(importData.created) || 0,
          updated: Number(importData.updated) || 0,
          skipped: Number(importData.skipped) || 0,
          errors: importData.errors.length
            ? importData.errors
            : [body?.message || 'Import failed — no rows applied'],
        };
      }
      const msg = body?.message || body?.error || ax.message || 'Import failed';
      throw new Error(msg);
    }
  }

  async listEmergencyContacts(employeeId: string): Promise<EmergencyContactRecord[]> {
    const response = await api.get(`/hr/employees/${employeeId}/emergency-contacts`);
    const raw = unwrap<EmergencyContactRecord[]>(response);
    return Array.isArray(raw) ? raw : [];
  }

  async createEmergencyContact(
    employeeId: string,
    body: Omit<EmergencyContactRecord, 'id' | 'employee_id'>
  ): Promise<EmergencyContactRecord> {
    const response = await api.post(`/hr/employees/${employeeId}/emergency-contacts`, body);
    return unwrap<EmergencyContactRecord>(response);
  }

  async updateEmergencyContact(
    employeeId: string,
    contactId: string,
    body: Partial<Omit<EmergencyContactRecord, 'id' | 'employee_id'>>
  ): Promise<EmergencyContactRecord> {
    const response = await api.put(`/hr/employees/${employeeId}/emergency-contacts/${contactId}`, body);
    return unwrap<EmergencyContactRecord>(response);
  }

  async deleteEmergencyContact(employeeId: string, contactId: string): Promise<void> {
    await api.delete(`/hr/employees/${employeeId}/emergency-contacts/${contactId}`);
  }

  async listProfileChangeRequestsForEmployee(employeeId: string): Promise<ProfileChangeRequestRecord[]> {
    const response = await api.get(`/hr/employees/${employeeId}/profile-change-requests`);
    const raw = unwrap<ProfileChangeRequestRecord[]>(response);
    return Array.isArray(raw) ? raw : [];
  }

  async listCompanyProfileChangeRequests(status?: string): Promise<ProfileChangeRequestRecord[]> {
    const response = await api.get('/hr/profile-change-requests', { params: { status } });
    const raw = unwrap<ProfileChangeRequestRecord[]>(response);
    return Array.isArray(raw) ? raw : [];
  }

  async createProfileChangeRequest(
    employeeId: string,
    body: { field_key: string; old_value_json?: string; new_value_json?: string }
  ): Promise<ProfileChangeRequestRecord> {
    const response = await api.post(`/hr/employees/${employeeId}/profile-change-requests`, body);
    return unwrap<ProfileChangeRequestRecord>(response);
  }

  async reviewProfileChangeRequest(
    id: string,
    body: { approve: boolean; review_comment?: string }
  ): Promise<ProfileChangeRequestRecord> {
    const response = await api.patch(`/hr/profile-change-requests/${id}/review`, body);
    return unwrap<ProfileChangeRequestRecord>(response);
  }

  async listLeaveBalances(employeeId: string): Promise<LeaveBalanceRecord[]> {
    const response = await api.get(`/hr/employees/${employeeId}/leave-balances`);
    const raw = unwrap<LeaveBalanceRecord[]>(response);
    return Array.isArray(raw) ? raw : [];
  }

  async upsertLeaveBalance(
    employeeId: string,
    body: {
      leave_year: number;
      leave_type: string;
      opening_balance?: number;
      consumed?: number;
      carried_forward?: number;
    }
  ): Promise<LeaveBalanceRecord> {
    const response = await api.put(`/hr/employees/${employeeId}/leave-balances`, body);
    return unwrap<LeaveBalanceRecord>(response);
  }

  async listSalaryComponents(employeeId: string): Promise<SalaryComponentRecord[]> {
    const response = await api.get(`/hr/employees/${employeeId}/salary-components`);
    const raw = unwrap<SalaryComponentRecord[]>(response);
    return Array.isArray(raw) ? raw : [];
  }

  async createSalaryComponent(
    employeeId: string,
    body: {
      component_code: string;
      component_name: string;
      amount: number;
      effective_from: string;
      effective_to?: string;
    }
  ): Promise<SalaryComponentRecord> {
    const response = await api.post(`/hr/employees/${employeeId}/salary-components`, body);
    return unwrap<SalaryComponentRecord>(response);
  }

  async deleteSalaryComponent(employeeId: string, id: string): Promise<void> {
    await api.delete(`/hr/employees/${employeeId}/salary-components/${id}`);
  }

  // ----- Enterprise dossier -----
  async listQualifications(employeeId: string): Promise<EmployeeQualificationRecord[]> {
    const response = await api.get(`/hr/employees/${employeeId}/qualifications`);
    const raw = unwrap<EmployeeQualificationRecord[]>(response);
    return Array.isArray(raw) ? raw : [];
  }

  async createQualification(
    employeeId: string,
    body: Omit<EmployeeQualificationRecord, 'id' | 'employee_id'>
  ): Promise<EmployeeQualificationRecord> {
    const response = await api.post(`/hr/employees/${employeeId}/qualifications`, body);
    return unwrap<EmployeeQualificationRecord>(response);
  }

  async updateQualification(
    employeeId: string,
    id: string,
    body: Partial<Omit<EmployeeQualificationRecord, 'id' | 'employee_id'>>
  ): Promise<EmployeeQualificationRecord> {
    const response = await api.put(`/hr/employees/${employeeId}/qualifications/${id}`, body);
    return unwrap<EmployeeQualificationRecord>(response);
  }

  async deleteQualification(employeeId: string, id: string): Promise<void> {
    await api.delete(`/hr/employees/${employeeId}/qualifications/${id}`);
  }

  async listPerformance(employeeId: string): Promise<EmployeePerformanceRecord[]> {
    const response = await api.get(`/hr/employees/${employeeId}/performance`);
    const raw = unwrap<EmployeePerformanceRecord[]>(response);
    return Array.isArray(raw) ? raw : [];
  }

  async createPerformance(
    employeeId: string,
    body: Omit<EmployeePerformanceRecord, 'id' | 'employee_id'>
  ): Promise<EmployeePerformanceRecord> {
    const response = await api.post(`/hr/employees/${employeeId}/performance`, body);
    return unwrap<EmployeePerformanceRecord>(response);
  }

  async updatePerformance(
    employeeId: string,
    id: string,
    body: Partial<Omit<EmployeePerformanceRecord, 'id' | 'employee_id'>>
  ): Promise<EmployeePerformanceRecord> {
    const response = await api.put(`/hr/employees/${employeeId}/performance/${id}`, body);
    return unwrap<EmployeePerformanceRecord>(response);
  }

  async deletePerformance(employeeId: string, id: string): Promise<void> {
    await api.delete(`/hr/employees/${employeeId}/performance/${id}`);
  }

  async listLifecycle(employeeId: string): Promise<EmployeeLifecycleEventRecord[]> {
    const response = await api.get(`/hr/employees/${employeeId}/lifecycle`);
    const raw = unwrap<EmployeeLifecycleEventRecord[]>(response);
    return Array.isArray(raw) ? raw : [];
  }

  async createLifecycle(
    employeeId: string,
    body: Omit<EmployeeLifecycleEventRecord, 'id' | 'employee_id'>
  ): Promise<EmployeeLifecycleEventRecord> {
    const response = await api.post(`/hr/employees/${employeeId}/lifecycle`, body);
    return unwrap<EmployeeLifecycleEventRecord>(response);
  }

  async updateLifecycle(
    employeeId: string,
    id: string,
    body: Partial<Omit<EmployeeLifecycleEventRecord, 'id' | 'employee_id'>>
  ): Promise<EmployeeLifecycleEventRecord> {
    const response = await api.put(`/hr/employees/${employeeId}/lifecycle/${id}`, body);
    return unwrap<EmployeeLifecycleEventRecord>(response);
  }

  async deleteLifecycle(employeeId: string, id: string): Promise<void> {
    await api.delete(`/hr/employees/${employeeId}/lifecycle/${id}`);
  }

  async listShiftAssignments(employeeId: string): Promise<EmployeeShiftAssignmentRecord[]> {
    const response = await api.get(`/hr/employees/${employeeId}/shift-assignments`);
    const raw = unwrap<EmployeeShiftAssignmentRecord[]>(response);
    return Array.isArray(raw) ? raw : [];
  }

  async createShiftAssignment(
    employeeId: string,
    body: { shift_id: string; effective_from: string; effective_to?: string }
  ): Promise<EmployeeShiftAssignmentRecord> {
    const response = await api.post(`/hr/employees/${employeeId}/shift-assignments`, body);
    return unwrap<EmployeeShiftAssignmentRecord>(response);
  }

  async endShiftAssignment(employeeId: string, id: string): Promise<EmployeeShiftAssignmentRecord> {
    const response = await api.delete(`/hr/employees/${employeeId}/shift-assignments/${id}`);
    return unwrap<EmployeeShiftAssignmentRecord>(response);
  }
}

export const employeeService = new EmployeeService();
export default employeeService;
